import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

export interface DailyView {
  date: string
  count: number
}

export interface TopPage {
  page_path: string
  count: number
}

export interface TopNews {
  title: string
  views: number
  slug: string
}

export interface AnalyticsData {
  todayViews: number
  yesterdayViews: number
  weekViews: number
  monthViews: number
  dailyViews: DailyView[]
  monthlyViews: DailyView[]
  topPages: TopPage[]
  topNews: TopNews[]
  uniqueSessionsToday: number
}

export function useAdminAnalytics() {
  const [data, setData] = useState<AnalyticsData>({
    todayViews: 0,
    yesterdayViews: 0,
    weekViews: 0,
    monthViews: 0,
    dailyViews: [],
    monthlyViews: [],
    topPages: [],
    topNews: [],
    uniqueSessionsToday: 0,
  })
  const [loading, setLoading] = useState(true)

  const fetchAnalytics = useCallback(async () => {
    setLoading(true)

    const today = new Date().toISOString().split('T')[0]
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0]
    const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString()
    const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString()

    // Today views
    const { data: todayData } = await supabase
      .from('page_views')
      .select('id', { count: 'exact' })
      .gte('created_at', `${today}T00:00:00Z`)

    // Yesterday views
    const { data: yesterdayData } = await supabase
      .from('page_views')
      .select('id', { count: 'exact' })
      .gte('created_at', `${yesterday}T00:00:00Z`)
      .lt('created_at', `${today}T00:00:00Z`)

    // Week views
    const { data: weekData } = await supabase
      .from('page_views')
      .select('id', { count: 'exact' })
      .gte('created_at', weekAgo)

    // Month views
    const { data: monthData } = await supabase
      .from('page_views')
      .select('id', { count: 'exact' })
      .gte('created_at', monthAgo)

    // Daily views for last 7 days
    const { data: dailyData } = await supabase
      .from('page_views')
      .select('created_at')
      .gte('created_at', weekAgo)
      .order('created_at', { ascending: true })

    // Monthly views for last 30 days
    const { data: monthlyData } = await supabase
      .from('page_views')
      .select('created_at')
      .gte('created_at', monthAgo)
      .order('created_at', { ascending: true })

    // Top pages
    const { data: topPagesData } = await supabase
      .from('page_views')
      .select('page_path')
      .gte('created_at', weekAgo)

    // Top news from news table
    const { data: topNewsData } = await supabase
      .from('news')
      .select('title, views, slug')
      .eq('status', 'publicado')
      .order('views', { ascending: false })
      .limit(10)

    // Unique sessions today
    const { data: sessionsData } = await supabase
      .from('page_views')
      .select('session_id')
      .gte('created_at', `${today}T00:00:00Z`)

    // Process daily views (7 days)
    const dailyMap = new Map<string, number>()
    for (let i = 6; i >= 0; i--) {
      const d = new Date(Date.now() - i * 86400000).toISOString().split('T')[0]
      dailyMap.set(d, 0)
    }
    ;(dailyData || []).forEach((row) => {
      const d = new Date(row.created_at).toISOString().split('T')[0]
      dailyMap.set(d, (dailyMap.get(d) || 0) + 1)
    })
    const dailyViews = Array.from(dailyMap.entries()).map(([date, count]) => ({
      date,
      count,
    }))

    // Process monthly views (30 days)
    const monthlyMap = new Map<string, number>()
    for (let i = 29; i >= 0; i--) {
      const d = new Date(Date.now() - i * 86400000).toISOString().split('T')[0]
      monthlyMap.set(d, 0)
    }
    ;(monthlyData || []).forEach((row) => {
      const d = new Date(row.created_at).toISOString().split('T')[0]
      monthlyMap.set(d, (monthlyMap.get(d) || 0) + 1)
    })
    const monthlyViews = Array.from(monthlyMap.entries()).map(([date, count]) => ({
      date,
      count,
    }))

    // Process top pages
    const pageMap = new Map<string, number>()
    ;(topPagesData || []).forEach((row) => {
      const path = row.page_path
      pageMap.set(path, (pageMap.get(path) || 0) + 1)
    })
    const topPages = Array.from(pageMap.entries())
      .map(([page_path, count]) => ({ page_path, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)

    // Unique sessions
    const uniqueSessions = new Set((sessionsData || []).map((s) => s.session_id))

    setData({
      todayViews: todayData?.length || 0,
      yesterdayViews: yesterdayData?.length || 0,
      weekViews: weekData?.length || 0,
      monthViews: monthData?.length || 0,
      dailyViews,
      monthlyViews,
      topPages,
      topNews: (topNewsData || []).map((n) => ({ title: n.title, views: n.views || 0, slug: n.slug })),
      uniqueSessionsToday: uniqueSessions.size,
    })
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchAnalytics()
  }, [fetchAnalytics])

  return { data, loading, refetch: fetchAnalytics }
}